import {
  ApplicationRef,
  Injectable,
  Injector,
  ReflectiveInjector,
  ComponentFactory,
  ComponentFactoryResolver,
  ComponentRef,
  TemplateRef
} from '@angular/core';

import { ContentRef } from '../util/popup';
import { isDefined, isString } from '../util/util';

import { FishModalBackdrop } from './modal-backdrop';
import { FishModalWindow } from './modal-window';
import { FishActiveModal, FishModalRef } from './modal-ref';


export class FishModalStack {
  private _backdropFactory: ComponentFactory<FishModalBackdrop>;
  private _windowFactory: ComponentFactory<FishModalWindow>;

  constructor(
    private _applicationRef: ApplicationRef, private _injector: Injector,
    private _componentFactoryResolver: ComponentFactoryResolver) {
    this._backdropFactory = _componentFactoryResolver.resolveComponentFactory(FishModalBackdrop);
    this._windowFactory = _componentFactoryResolver.resolveComponentFactory(FishModalWindow);
  }

  open(moduleCFR: ComponentFactoryResolver, contentInjector: Injector, content: any, options): FishModalRef {
    const containerSelector = options.container || 'body';
    const containerEl = document.querySelector(containerSelector);

    if (!containerEl) {
      throw new Error(`The specified modal container "${containerSelector}" was not found in the DOM.`);
    }

    const activeModal = new FishActiveModal();
    const contentRef = this._getContentRef(moduleCFR, contentInjector, content, activeModal);

    let windowCmptRef: ComponentRef<FishModalWindow>;
    let backdropCmptRef: ComponentRef<FishModalBackdrop>;
    let fishModalRef: FishModalRef;


    if (options.backdrop !== false) {
      backdropCmptRef = this._backdropFactory.create(this._injector);
      this._applicationRef.attachView(backdropCmptRef.hostView);
      containerEl.appendChild(backdropCmptRef.location.nativeElement);
    }
    windowCmptRef = this._windowFactory.create(this._injector, contentRef.nodes);
    this._applicationRef.attachView(windowCmptRef.hostView);
    containerEl.appendChild(windowCmptRef.location.nativeElement);

    fishModalRef = new FishModalRef(windowCmptRef, contentRef, backdropCmptRef);

    activeModal.close = (result: any) => { fishModalRef.close(result); };
    activeModal.dismiss = (reason: any) => { fishModalRef.dismiss(reason); };

    this._applyWindowOptions(windowCmptRef.instance, options);

    return fishModalRef;
  }

  private _applyWindowOptions(windowInstance: FishModalWindow, options: Object): void {
    ['backdrop', 'keyboard', 'size', 'windowClass'].forEach((optionName: string) => {
      if (isDefined(options[optionName])) {
        windowInstance[optionName] = options[optionName];
      }
    });
  }

  private _getContentRef(
    moduleCFR: ComponentFactoryResolver, contentInjector: Injector, content: any,
    context: FishActiveModal): ContentRef {
    if (!content) {
      return new ContentRef([]);
    } else if (content instanceof TemplateRef) {
      const viewRef = content.createEmbeddedView(context);
      this._applicationRef.attachView(viewRef);
      return new ContentRef([viewRef.rootNodes], viewRef);
    } else if (isString(content)) {
      return new ContentRef([[document.createTextNode(`${content}`)]]);
    } else {
      const contentCmptFactory = moduleCFR.resolveComponentFactory(content);
      const modalContentInjector =
        ReflectiveInjector.resolveAndCreate([{ provide: FishActiveModal, useValue: context }], contentInjector);
      const componentRef = contentCmptFactory.create(modalContentInjector);
      this._applicationRef.attachView(componentRef.hostView);
      return new ContentRef([[componentRef.location.nativeElement]], componentRef.hostView, componentRef);
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ApplicationRef, },
{type: Injector, },
{type: ComponentFactoryResolver, },
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
